import { useEffect, useState } from 'react'
import { union } from 'lodash-es'

import { authedDelete, authedGet, authedPost } from '../lib/intranet_tools_service'
import { Approver, LoginType } from './types'
import { Tooltip } from './Tooltip'
import './ApproversSelector.css'

type AddApproverModalProps = {
  loadAvUsers: () => Promise<void>
  onClose: () => void
}

const loginTypeToolTip = `Users with password login type can login to COPRA and access all views.
Users with email login type can only access timesheets via the email notification sent when a timesheet is submitted.`

function AddApproverModal({ loadAvUsers, onClose }: AddApproverModalProps) {
  const [approver, setApprover] = useState<Approver>({
    Name: '',
    Email: '',
    LoginType: 'PASSWORD',
  })
  const [error, setError] = useState('')

  const createUserHandler = async () => {
    const result = await authedPost('av/users', JSON.stringify(approver))
    const json = await result.json()

    if (!result.ok || json.status === 'FAILED') {
      setError(json.message || json.error || 'Failed to create approver')
      return
    }

    setError('')
    await loadAvUsers()
    onClose()
  }

  return (
    <>
      <div className="cg-modal-background nested"></div>
      <div className="cg-modal-new-dialog cg-modal-details-dialog">
        <div className="cg-modal-title">New Approver</div>
        <div className="cg-modal-content cg-modal-content-new-professional">
          {error && <div className="copra-error">{error}</div>}
          <div className="copra-modal-grid">
            <label className="copra-field">
              <span>Name</span>
              <input
                value={approver.Name}
                onChange={(e) =>
                  setApprover({
                    ...approver,
                    Name: e.target.value,
                  })
                }
              />
            </label>
            <label className="copra-field">
              <span>Email</span>
              <input
                value={approver.Email}
                onChange={(e) =>
                  setApprover({
                    ...approver,
                    Email: e.target.value,
                  })
                }
              />
            </label>
            <label className="copra-field copra-field-full">
              <span className="copra-inline-label">
                Login type
                <Tooltip
                  className="copra-approver-tooltip"
                  text={loginTypeToolTip}
                  width={350}
                />
              </span>
              <select
                value={approver.LoginType}
                onChange={(e) =>
                  setApprover({
                    ...approver,
                    LoginType: e.target.value as LoginType,
                  })
                }
              >
                <option value="PASSWORD">Password</option>
                <option value="EMAIL">Email</option>
              </select>
            </label>
          </div>
          <div className="cg-modal-actions">
            <button className="cg-modal-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="cg-modal-button" onClick={createUserHandler}>
              Create
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

type Props = {
  approvers: string[]
  setApprovers: (approvers: string[]) => void
}

const primaryApproverToolTip = `All approvers have 'APPROVER' permissions.
The primary approver will receive email notifications for all timesheets associated with the project.`

export function ApproversSelector({ approvers, setApprovers }: Props) {
  const [avUsers, setAvUsers] = useState<Approver[]>([])
  const [selectedAllApprovers, setSelectedAllApprovers] = useState<string[]>([])
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([])
  const [addApproverModalShown, showAddApproverModal] = useState(false)

  const loadAvUsers = async () => {
    const result = await authedGet('av/users')
    const json = await result.json()
    setAvUsers(json.data || [])
  }

  const addSelectedApprovers = () => {
    setApprovers(union(approvers, selectedAllApprovers))
  }

  const removeSelectedApprovers = () => {
    setApprovers(
      approvers.filter((id) => !selectedApprovers.find((x) => x === id))
    )
  }

  const deleteApprover = async () => {
    if (selectedAllApprovers.length !== 1) {
      alert('Please select one approver to delete')
      return
    }

    if (!window.confirm('Are you sure you want to delete this approver?')) {
      return
    }

    await authedDelete(`av/users/${selectedAllApprovers[0]}`, JSON.stringify({}))
    await loadAvUsers()
  }

  const getSelectedOptions = (options: any) => {
    const values = []
    for (let i = 0, l = options.length; i < l; i++) {
      if (options[i].selected) {
        values.push(options[i].value)
      }
    }
    return values
  }

  const getApproverName = (id: string) => {
    return avUsers.find((user) => user.ID === id)?.Name || id
  }

  const setPrimaryApprover = () => {
    if (selectedApprovers.length !== 1) {
      window.alert('Please select one approver to make the primary.')
      return
    }

    const selectedId = selectedApprovers[0]
    const selectedIdIndexInApprovers = approvers.findIndex((id) => id === selectedId)
    const newApprovers = approvers.slice()
    newApprovers.splice(selectedIdIndexInApprovers, 1)
    newApprovers.unshift(selectedId)
    setApprovers(newApprovers)
  }

  useEffect(() => {
    loadAvUsers()
  }, [])

  if (avUsers.length === 0) {
    return <div>Loading approvers</div>
  }

  return (
    <div>
      {addApproverModalShown && (
        <AddApproverModal
          loadAvUsers={loadAvUsers}
          onClose={() => showAddApproverModal(false)}
        />
      )}
      <div className="copra-approver-section">
        <div className="copra-approver-header">
          <div className="label copra-approver-title">
            <span>Approver</span>
            <button
              className="copra-approver-icon-button"
              onClick={() => showAddApproverModal(true)}
              type="button"
              title="New approver"
              aria-label="New approver"
            >
              <div className="copra-approver-icon-shell">
                <svg
                  className="copra-approver-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.6 9.9v1.8m0 0v1.8m0-1.8h1.8m-1.8 0h-1.8m-1.35-2.475a2.025 2.025 0 1 1-4.05 0a2.025 2.025 0 0 1 4.05 0ZM6.6 16.315v-.066a3.825 3.825 0 0 1 7.65 0v.0654A7.389 7.389 0 0 1 10.125 17.1c-1.3995 0-2.7-.387-3.525-.785Z"
                  />
                </svg>
              </div>
            </button>
            <button
              className="copra-approver-icon-button"
              onClick={deleteApprover}
              type="button"
              title="Delete approver"
              aria-label="Delete approver"
            >
              <div className="copra-approver-icon-shell">
                <svg
                  className="copra-approver-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.4 11.1h-3.6m-1.35-2.475a2.025 2.025 0 1 1-4.05 0 2.025 2.025 0 0 1 4.05 0ZM6.6 16.341v-.066a3.825 3.825 0 0 1 7.65 0v.0654A7.3908 7.3908 0 0 1 10.4244 17.4c-1.3986 0-2.7072-.387-3.8244-1.0596Z"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <div className="copra-approver-layout">
          <div className="value">
            <select
              className="approver-list"
              onChange={(e) => setSelectedAllApprovers(getSelectedOptions(e.target.options))}
              value={selectedAllApprovers}
              multiple
            >
              {avUsers.map((avUser) => (
                <option key={avUser.ID} value={avUser.ID}>
                  {avUser.Name}
                </option>
              ))}
            </select>
          </div>

          <div className="value approver-btns copra-approver-buttons">
            <button
              className="cg-modal-button secondary copra-approver-stack-btn"
              onClick={addSelectedApprovers}
              type="button"
            >
              Add
            </button>
            <button
              className="cg-modal-button secondary copra-approver-stack-btn"
              onClick={removeSelectedApprovers}
              type="button"
            >
              Remove
            </button>
          </div>

          <div className="value">
            <select
              className="approver-list approver-list-selected"
              onChange={(e) => setSelectedApprovers(getSelectedOptions(e.target.options))}
              value={selectedApprovers}
              multiple
            >
              {approvers.map((approver) => (
                <option key={approver} value={approver}>
                  {getApproverName(approver)}
                </option>
              ))}
            </select>
          </div>

          <div className="value approver-btns copra-approver-buttons">
            <div className="copra-primary-row">
              <button
                className="cg-modal-button secondary copra-approver-stack-btn"
                onClick={setPrimaryApprover}
                type="button"
              >
                Make primary
              </button>
              <Tooltip
                className="copra-approver-tooltip"
                text={primaryApproverToolTip}
                width={350}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
